import React, { useMemo } from 'react';
import {
  HiddenContent,
  SideNavButtonInnerWrapper,
  SideNavLinkFeaturesSlot,
  SideNavLinkOuterWrapper,
  SideNavLinkTitleWrapper,
  SvgWrapper,
} from './styles';
import { SvgIcon } from 'src/commons/components/SvgIcon/SvgIcon';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import { NavMetricPublisher } from 'src/commons/utils/metrics';
import SideNavLinkFeatures from 'src/features/nav/components/ConsolidatedSideNav/SideNavLinkFeatures/SideNavLinkFeatures';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import { useInterstitialModalIntercept } from './InterstitialModal/useInterstitialModalIntercept';
import { getFirstSideNavLink } from 'src/features/nav/utils/toolbarItemLinks';

interface SideNavButtonProps {
  id: string;
  allToolsEnabled: boolean;
}

const SideNavButton: React.VFC<SideNavButtonProps> = ({
  id,
  allToolsEnabled,
}) => {
  const { isNavOpen, toolbarItems, setOpenModalAppName } =
    useConsolidatedNavContext();
  const tryInterceptInterstitial = useInterstitialModalIntercept();

  const toolbarItem = useMemo(
    () => toolbarItems.find((toolbarItem) => toolbarItem.id === id),
    [toolbarItems],
  );

  const targetUrl = toolbarItem?.sidebarGroups?.[0]?.links?.[0]?.link?.url;
  const appName = toolbarItem?.sidebarGroups?.[0]?.links?.[0]?.appName ?? '';

  const onClickHandler = () => {
    if (!toolbarItem) {
      return;
    }
    NavMetricPublisher.publishCounterMonitor(`toolbarItemClicked-${id}`, 1);
    if (tryInterceptInterstitial(targetUrl, appName)) {
      return;
    }
    setOpenModalAppName(appName);
  };

  if (!toolbarItem) {
    return null;
  }
  const firstLink = getFirstSideNavLink(toolbarItem);
  if (!firstLink) {
    return null;
  }
  const { link, id: linkId } = firstLink;

  const { label: title, url } = link;

  return (
    <SideNavLinkOuterWrapper isSelected={false} isNavOpen={isNavOpen}>
      <SideNavButtonInnerWrapper
        isNavOpen={isNavOpen}
        isSelected={false}
        data-ccx-e2e-id={linkId}
        aria-label={title}
        onClick={onClickHandler}
        {...getTaktAttributes(
          linkId,
          chromeComponents.NAV,
          { url: url },
          allToolsEnabled,
        )}
      >
        <SvgWrapper
          isSelected={false}
          data-ccx-e2e-id={id}
          alignItems="center"
          justifyContent="center"
        >
          <SvgIcon
            iconPath={toolbarItem.iconPath}
            ariaLabel={title}
            isSelected={false}
          />
        </SvgWrapper>
        <HiddenContent alignItems="center">
          <SideNavLinkTitleWrapper>{title}</SideNavLinkTitleWrapper>
        </HiddenContent>
      </SideNavButtonInnerWrapper>
      <SideNavLinkFeaturesSlot alignItems="center">
        <SideNavLinkFeatures sideNavLink={firstLink} isFocusable />
      </SideNavLinkFeaturesSlot>
    </SideNavLinkOuterWrapper>
  );
};

export default SideNavButton;
